import React, { useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { SmoothScroll } from "./components/SmoothScroll";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Features from "./components/Features";
import Marquee from "./components/Marquee";
import Newsletter from "./components/Newsletter";
import Footer from "./components/Footer";
import FAQ from "./components/FAQ";
import ValuePropSplit from "./components/ValuePropSplit";
import TrustMoat from "./components/TrustMoat";
import FounderNote from "./components/FounderNote";
import Mission from "./components/Mission";
import HappeningsStrip from "./components/HappeningsStrip";
import { PaperGrain } from "./components/editorial";
import SocialProofTicker from "./components/SocialProofTicker";
import AnalyticsScripts from "./components/AnalyticsScripts";
import ClusterTagBinder from "./components/ClusterTagBinder";
import ConsentBanner from "./components/ConsentBanner";
import HowItWorks from "./components/HowItWorks";
// Testimonials + StatsBand are held (unwired) until real content exists; see below.
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import ChildSafetyPage from "./pages/ChildSafetyPage";
import PeoplePage from "./pages/PeoplePage";
import EventsPage from "./pages/EventsPage";
import EventRsvpPage from "./pages/EventRsvpPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import AboutPage from "./pages/AboutPage";
import CommunitiesPage from "./pages/CommunitiesPage";
import SafetyPage from "./pages/SafetyPage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogPostPage from "./pages/BlogPostPage";
import DownloadPage from "./pages/DownloadPage";
import BetaPage from "./pages/BetaPage";
import SurveyPage from "./pages/SurveyPage";
import SurveyFullPage from "./pages/SurveyFullPage";
import SurveyShortPage from "./pages/SurveyShortPage";
import SupportPage from "./pages/SupportPage";
import StoryHomePage from "./pages/StoryHomePage";
import NotFoundPage from "./pages/NotFoundPage";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";

const Home: React.FC = () => {
  const [currentPage, setCurrentPage] = useState("home");

  const renderPage = () => {
    switch (currentPage) {
      case "mission":
        return <Mission />;
      case "home":
      default:
        return (
          <>
            <Hero />
            <HappeningsStrip />
            <Marquee />
            <ValuePropSplit />
            <HowItWorks />
            <TrustMoat />
            <Features />
            {/* Testimonials + StatsBand held until real member quotes and real numbers exist (no fabricated claims, ASA-safe). Components + data live in components/Testimonials.tsx, components/StatsBand.tsx, content/home-sections.ts. Re-add <Testimonials /> + <StatsBand /> here once that data is real. */}
            <FounderNote />
            <FAQ />
            <Newsletter />
          </>
        );
    }
  };

  return (
    <>
      <Navbar onNavigate={setCurrentPage} currentPage={currentPage} />

      <main className="flex-grow relative bg-paper">
        <PaperGrain />
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>
      </main>

      <Footer onNavigate={setCurrentPage} />
      <SocialProofTicker />
    </>
  );
};

// LL-0184 2026-07-06: /survey renamed to /beta-review. Kept as a silent
// redirect (preserving ?g=/&n= query params) rather than removed outright,
// since wave-1 tester links using the old path may already be sent.
const LegacySurveyRedirect: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

// Carries the slug across the /blog -> /news rename for client-side hits.
const LegacyBlogPostRedirect: React.FC = () => {
  const { slug } = useParams();
  return <Navigate to={`/news/${slug}`} replace />;
};

const App: React.FC = () => {
  return (
    // reducedMotion="user" makes every framer-motion animation on the site honour
    // the OS "Reduce motion" setting (transform/opacity tweens are skipped, values
    // jump to their end state). Pairs with SmoothScroll + Marquee, which gate their
    // own non-framer animations on the same media query. WCAG 2.3.3 (LL-0019).
    <MotionConfig reducedMotion="user">
      <SmoothScroll>
        {/* Resets scroll on route change. Must sit INSIDE SmoothScroll so Lenis
            already exists when it runs, and inside the Router so it can see the
            location. */}
        <ScrollToTop />
        <div className="min-h-screen bg-canvas text-ink flex flex-col font-sans selection:bg-salmon selection:text-ink">
          <Routes>
            {/* STORY SITE IS NOW THE HOME PAGE on this branch. The old home is
                kept at /classic so the two can still be compared side by side,
                and so reverting is a one-line swap rather than a git revert.
                Serving the new page at /next while the navbar's Home pointed at
                the old one made the nav look broken, which is what forced this. */}
            <Route path="/" element={<StoryHomePage />} />
            <Route path="/classic" element={<Home />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            {/* The public company page: identity, business model, funding
                position and team. Carries the evidence Google for Startups
                asked for, and is linked from the header and the footer so a
                reviewer can reach it without hunting. */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/communities" element={<CommunitiesPage />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            {/* Google Play requires a public account-deletion URL reachable
                without installing the app. Must stay ABOVE the "*" catch-all,
                which would otherwise render Home here and look like a live page. */}
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            {/* Google Play's Child Safety Standards policy requires social apps
                to publish their CSAE standards at a public URL and cite it in
                the Play Console declaration. Same catch-all rule as above. */}
            <Route path="/child-safety" element={<ChildSafetyPage />} />
            <Route path="/people/:slug" element={<PeoplePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:slug" element={<EventRsvpPage />} />
            <Route path="/news" element={<BlogIndexPage />} />
            <Route path="/news/:slug" element={<BlogPostPage />} />
            {/* /blog moved to /news (2026-08-01). The REAL redirect is a 301 in
                vercel.json, because only that transfers the ranking of posts
                that are already indexed and shared. These two exist so a
                client-side navigation to an old in-app link still lands
                correctly, and so local `vite preview`, which has no edge
                redirects, behaves like production. */}
            <Route path="/blog" element={<Navigate to="/news" replace />} />
            <Route path="/blog/:slug" element={<LegacyBlogPostRedirect />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/beta" element={<BetaPage />} />
            <Route path="/beta-review" element={<SurveyPage />} />
            <Route path="/beta-review/full" element={<SurveyFullPage />} />
            <Route path="/beta-review/short" element={<SurveyShortPage />} />
            <Route
              path="/survey"
              element={<LegacySurveyRedirect to="/beta-review" />}
            />
            <Route
              path="/survey/full"
              element={<LegacySurveyRedirect to="/beta-review/full" />}
            />
            {/* LL-0184 2026-07-06 (later): /beta-review-short was shipped as a
                flat hyphenated route before the /beta-review/full path-segment
                convention was noticed as the established pattern. Renamed to
                /beta-review/short for consistency (variants of one resource,
                not a separate top-level page); old path kept as a redirect in
                case it's already been sent. */}
            <Route
              path="/beta-review-short"
              element={<LegacySurveyRedirect to="/beta-review/short" />}
            />
            <Route path="/support" element={<SupportPage />} />
            {/* Story-site revamp preview (LL-0199). Not linked, not indexed. */}
            <Route path="/next" element={<StoryHomePage />} />{" "}
            {/* kept: shared links point here */}
            {/* Real 404 (was <Home />, which turned every unknown URL into a
                soft 404). prerender.mjs renders this route into dist/404.html;
                with the vercel.json catch-all rewrite gone, Vercel serves that
                file with a genuine 404 status. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <AnalyticsScripts />
          <ClusterTagBinder />
          <ConsentBanner />
          <SpeedInsights />
          <Analytics />
        </div>
      </SmoothScroll>
    </MotionConfig>
  );
};

export default App;
